import React, { useState, useEffect } from 'react';
import Weather from './Weather';

const DAYS = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
const DAYS_SHORT = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const MONTHS = ['January','February','March','April','May','June',
                 'July','August','September','October','November','December'];
const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun',
                       'Jul','Aug','Sep','Oct','Nov','Dec'];

export default function Header({ onLogout, onManage, onThemeToggle, theme, isMobile, view, onViewChange, views }) {
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const h = now.getHours();
  const m = String(now.getMinutes()).padStart(2, '0');
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  const isLight = theme === 'light';

  if (isMobile) {
    return (
      <header style={styles.headerMobile}>
        <div style={styles.mobileLeft}>
          <span style={styles.dayNameMobile}>{DAYS_SHORT[now.getDay()]}</span>
          <span style={styles.dateMobile}>{MONTHS_SHORT[now.getMonth()]} {now.getDate()}</span>
        </div>
        <Weather compact />
        <div style={styles.mobileRight}>
          <span style={styles.timeMobile}>{h12}:{m}</span>
          <span style={styles.ampmMobile}>{ampm}</span>
          {onThemeToggle && (
            <button onClick={onThemeToggle} style={styles.iconBtn} title="Toggle theme">
              {isLight ? '🌙' : '☀️'}
            </button>
          )}
          {onManage && (
            <button onClick={onManage} style={styles.iconBtn} title="Manage">⚙️</button>
          )}
          {onLogout && (
            <button onClick={onLogout} style={styles.logoutBtnMobile} title="Sign out">⏻</button>
          )}
        </div>
      </header>
    );
  }

  return (
    <header style={styles.header}>
      {/* Left: date */}
      <div style={styles.dateBlock}>
        <span style={styles.dayName}>{DAYS[now.getDay()]}</span>
        <span style={styles.date}>
          {MONTHS[now.getMonth()]} {now.getDate()}, {now.getFullYear()}
        </span>
      </div>

      {/* Center: title + weather + nav */}
      <div style={styles.centerBlock}>
        <div style={styles.titleRow}>
          <span style={styles.titlePrefix}>◈</span>
          <span style={styles.title}>Smith Family Command Center</span>
          <span style={styles.titlePrefix}>◈</span>
        </div>
        <div style={styles.centerBottom}>
          <Weather />
          {views && (
            <div style={styles.navTabs}>
              {views.map(v => (
                <button
                  key={v.id}
                  style={{...styles.navTab, ...(view === v.id ? styles.navTabActive : {})}}
                  onClick={() => onViewChange(v.id)}
                >
                  {v.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Right: time + controls */}
      <div style={styles.rightBlock}>
        <div style={styles.clock}>
          <span style={styles.time}>{h12}:{m}</span>
          <span style={styles.ampm}>{ampm}</span>
        </div>
        <div style={styles.controls}>
          {onThemeToggle && (
            <button onClick={onThemeToggle} style={styles.controlBtn} title="Toggle theme">
              {isLight ? '🌙' : '☀️'}
            </button>
          )}
          {onManage && (
            <button onClick={onManage} style={styles.controlBtn} title="Manage">⚙️</button>
          )}
          {onLogout && (
            <button onClick={onLogout} style={styles.logoutBtn} title="Sign out">⏻</button>
          )}
        </div>
      </div>
    </header>
  );
}

const styles = {
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 24px 10px',
    borderBottom: '1px solid var(--header-border)',
    flexShrink: 0,
    background: 'var(--surface)',
    gap: 16,
  },
  headerMobile: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px 10px',
    borderBottom: '1px solid var(--header-border)',
    flexShrink: 0,
    background: 'var(--surface)',
  },
  dateBlock: {
    display: 'flex', flexDirection: 'column', gap: 2, flexShrink: 0,
  },
  dayName: {
    fontFamily: 'var(--font-display)',
    fontSize: '1.4rem', color: 'var(--accent)',
    lineHeight: 1, fontStyle: 'italic',
  },
  date: {
    fontFamily: 'var(--font-mono)', fontSize: '0.68rem',
    color: 'var(--text-dim)', letterSpacing: '0.08em', textTransform: 'uppercase',
  },

  // Center block
  centerBlock: {
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', gap: 8, flex: 1,
  },
  titleRow: {
    display: 'flex', alignItems: 'center', gap: 10,
  },
  title: {
    fontFamily: 'var(--font-mono)',
    fontSize: '0.9rem',
    fontWeight: 500,
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    color: 'var(--accent)',
    textShadow: '0 0 20px rgba(232,201,122,0.3)',
  },
  titlePrefix: {
    color: 'var(--accent)',
    fontSize: '0.75rem',
    opacity: 0.5,
  },
  centerBottom: {
    display: 'flex', alignItems: 'center', gap: 16,
  },
  navTabs: {
    display: 'flex', gap: 4,
  },
  navTab: {
    fontFamily: 'var(--font-mono)', fontSize: '0.68rem',
    letterSpacing: '0.1em', textTransform: 'uppercase',
    color: 'var(--text-dimmer)', background: 'transparent',
    border: '1px solid transparent', borderRadius: 20,
    padding: '8px 16px', cursor: 'pointer',
    transition: 'all 0.15s', minHeight: 36,
    display: 'flex', alignItems: 'center',
  },
  navTabActive: {
    color: 'var(--accent)',
    border: '1px solid var(--accent)',
    background: 'rgba(232,201,122,0.08)',
  },

  // Right block
  rightBlock: {
    display: 'flex', flexDirection: 'column',
    alignItems: 'flex-end', gap: 4, flexShrink: 0,
  },
  clock: {
    display: 'flex', alignItems: 'baseline', gap: 5,
  },
  time: {
    fontFamily: 'var(--font-display)',
    fontSize: '2rem', color: 'var(--text)', lineHeight: 1,
  },
  ampm: {
    fontFamily: 'var(--font-mono)', fontSize: '0.72rem',
    color: 'var(--text-dim)', letterSpacing: '0.1em',
  },
  controls: {
    display: 'flex', gap: 4, alignItems: 'center',
  },
  controlBtn: {
    background: 'none', border: '1px solid var(--border)',
    borderRadius: 10, color: 'var(--text-dim)',
    fontSize: '1rem', cursor: 'pointer',
    padding: '8px 12px', lineHeight: 1,
    minHeight: 40, minWidth: 40,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  logoutBtn: {
    background: 'rgba(232,122,122,0.12)',
    border: '1px solid rgba(232,122,122,0.4)',
    borderRadius: 10, color: '#e87a7a',
    fontSize: '1rem', cursor: 'pointer',
    padding: '8px 12px', lineHeight: 1,
    minHeight: 40, minWidth: 40,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },

  // Mobile styles
  mobileLeft: { display: 'flex', flexDirection: 'column', gap: 1 },
  dayNameMobile: {
    fontFamily: 'var(--font-display)', fontSize: '1.1rem',
    color: 'var(--accent)', lineHeight: 1, fontStyle: 'italic',
  },
  dateMobile: {
    fontFamily: 'var(--font-mono)', fontSize: '0.62rem',
    color: 'var(--text-dim)', letterSpacing: '0.08em', textTransform: 'uppercase',
  },
  mobileRight: { display: 'flex', alignItems: 'center', gap: 5 },
  timeMobile: {
    fontFamily: 'var(--font-display)', fontSize: '1.4rem',
    color: 'var(--text)', lineHeight: 1,
  },
  ampmMobile: {
    fontFamily: 'var(--font-mono)', fontSize: '0.62rem',
    color: 'var(--text-dim)', letterSpacing: '0.1em',
  },
  iconBtn: {
    background: 'none', border: 'none', fontSize: '1rem',
    cursor: 'pointer', padding: '4px', lineHeight: 1,
  },
  logoutBtnMobile: {
    background: 'rgba(232,122,122,0.12)',
    border: '1px solid rgba(232,122,122,0.4)',
    borderRadius: 8, color: '#e87a7a',
    fontSize: '0.9rem', cursor: 'pointer', padding: '4px 8px',
  },
};
